import { Controller } from "stimulus"
import { Calendar } from "@fullcalendar/core"
import dayGridPlugin from "@fullcalendar/daygrid"
import interactionPlugin from "@fullcalendar/interaction"

// Calendar is desktop-only. At/below this width the grid is hidden (SCSS) and the view falls back to
// the List. Keep in sync with the matching `@media (max-width: 768px)` in _program_events_filter_popover.scss.
const MOBILE_MEDIA_QUERY = "(max-width: 768px)"
const LIST_FRAME_ID = "program-events-list"
const PILLS_ISLAND_ID = "program-events-calendar-pills"
const LIST_LOADING_CLASS = "program-events-list--loading"

// Wraps the FullCalendar month grid on the Program Events index (calendar view).
//   - Pills are embedded in the page, never fetched: the visible month's pills arrive as a Stimulus
//     value on mount, and as a data island inside the list frame on month nav. We push them straight
//     into the grid — there is no JSON feed.
//   - Month nav (datesSet) re-points the list turbo-frame's `src`; the server re-renders the table
//     AND embeds the new month's pills in one response, which we adopt when the frame re-renders.
//   - Clicking a day narrows the list frame (&day=) and prefills the create-event link; the grid is
//     left as-is (a day drill-down embeds no pills). Clicking the selected day again deselects it,
//     restoring the month-scoped list. Month nav / Today clears the selection.
//   - Clicking a pill navigates to the event show page.
//   - Every calendar-driven list update (month nav / Today / day click / deselect) veils the list
//     frame with its own spinner until the frame re-renders; pager/filter navigations don't get it.
export default class extends Controller {
  static targets = ["grid"]
  static values = {
    pills: Array, initialDate: String, listUrl: String, listModeUrl: String, todayLabel: String, moreLabel: String
  }

  connect() {
    this.mobileQuery = window.matchMedia(MOBILE_MEDIA_QUERY)
    this.fallBackToListOnMobile = (event) => { if (event.matches) window.location.assign(this.listModeUrlValue) }
    this.mobileQuery.addEventListener("change", this.fallBackToListOnMobile)

    if (this.mobileQuery.matches) {
      this.fallBackToListOnMobile(this.mobileQuery)
      return
    }

    this.ready = false
    this.onListFrameRender = this.onListFrameRender.bind(this)
    document.addEventListener("turbo:frame-render", this.onListFrameRender)

    this.calendar = new Calendar(this.gridTarget, {
      plugins: [dayGridPlugin, interactionPlugin],
      initialView: "dayGridMonth",
      initialDate: this.initialDateValue,
      firstDay: 0,
      fixedWeekCount: false,
      showNonCurrentDates: true,
      headerToolbar: { left: "title", right: "prev today next" },
      buttonText: { today: this.todayLabelValue },
      dayMaxEvents: 3,
      moreLinkContent: (arg) => `+${arg.num} ${this.moreLabelValue}`,
      eventDisplay: "block",
      dateClick: (info) => this.onDateClick(info),
      datesSet: (info) => this.onDatesSet(info),
      eventClick: (info) => this.onEventClick(info),
      eventClassNames: (arg) => this.pillClasses(arg),
      eventContent: (arg) => this.renderPill(arg)
    })

    this.calendar.render()
    this.setPills(this.pillsValue)
  }

  disconnect() {
    if (this.mobileQuery && this.fallBackToListOnMobile) {
      this.mobileQuery.removeEventListener("change", this.fallBackToListOnMobile)
    }

    if (this.onListFrameRender) document.removeEventListener("turbo:frame-render", this.onListFrameRender)

    if (this.calendar) this.calendar.destroy()
  }

  setPills(pills) {
    if (this.eventSource) this.eventSource.remove()

    this.eventSource = this.calendar.addEventSource(pills || [])
  }

  onDatesSet(info) {
    const { start, end } = this.monthWindow(info.view)
    // Only the first datesSet for a given month refreshes the list — FullCalendar can emit it more
    // than once while settling layout, and a no-op re-fetch of the same month would break the
    // one-request-per-nav guarantee (the initial month is already server-rendered, so ready guards it).
    const monthChanged = start !== this.windowStart || end !== this.windowEnd

    this.windowStart = start
    this.windowEnd = end

    this.syncFilterWindow()
    this.highlightDay(null)
    this.updateCreateLink(null)

    if (this.ready && monthChanged) {
      this.setLoading(true)
      this.updateList()
    }

    this.ready = true
  }

  // Point the filter UI at the month being viewed, so applying / removing / resetting a filter
  // re-renders THIS month instead of snapping back to the initial one.
  syncFilterWindow() {
    this.setFilterWindowField("window_start", this.windowStart)
    this.setFilterWindowField("window_end", this.windowEnd)

    document.querySelectorAll(".active-filter-chip__remove").forEach((link) => {
      const url = new URL(link.href, window.location.origin)

      url.searchParams.set("window_start", this.windowStart)
      url.searchParams.set("window_end", this.windowEnd)
      link.setAttribute("href", url.pathname + url.search)
    })
  }

  setFilterWindowField(name, value) {
    const field = document.querySelector(`#program-events-filter-popover input[name="${name}"]`)

    if (field) field.value = value
  }

  // A click on the selected day deselects it; any other day selects it. Deselecting re-points the
  // list frame at the month scope — the same request a month nav fires — so the table widens back out.
  onDateClick(info) {
    if (info.dayEl.classList.contains("fc-day--selected")) {
      this.highlightDay(null)
      this.updateList()
      this.updateCreateLink(null)
      return
    }

    this.highlightDay(info.dayEl)
    this.updateList(info.dateStr)
    this.updateCreateLink(info.dateStr)
  }

  updateList(day = null) {
    const frame = document.getElementById(LIST_FRAME_ID)

    if (!frame) return

    const url = new URL(this.listUrlValue, window.location.origin)

    url.searchParams.set("window_start", this.windowStart)
    url.searchParams.set("window_end", this.windowEnd)

    if (day) url.searchParams.set("day", day)

    frame.classList.add(LIST_LOADING_CLASS)
    frame.setAttribute("src", url.pathname + url.search)
  }

  onListFrameRender(event) {
    if (event.target.id !== LIST_FRAME_ID) return

    const island = event.target.querySelector(`#${PILLS_ISLAND_ID}`)

    if (island) this.setPills(JSON.parse(island.dataset.pills))

    this.setLoading(false)
    event.target.classList.remove(LIST_LOADING_CLASS)
  }

  monthWindow(view) {
    const start = view.currentStart
    const lastDay = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0))

    return { start: this.isoDate(start), end: this.isoDate(lastDay) }
  }

  isoDate(date) {
    const month = String(date.getUTCMonth() + 1).padStart(2, "0")
    const day = String(date.getUTCDate()).padStart(2, "0")

    return `${date.getUTCFullYear()}-${month}-${day}`
  }

  setLoading(isLoading) {
    this.element.classList.toggle("program-events-calendar--loading", isLoading)
  }

  highlightDay(dayEl) {
    this.element.querySelectorAll(".fc-day--selected").forEach((el) => el.classList.remove("fc-day--selected"))

    if (dayEl) dayEl.classList.add("fc-day--selected")
  }

  updateCreateLink(day) {
    const link = document.getElementById("program-events-create-link")

    if (!link) return

    const url = new URL(link.href, window.location.origin)

    if (day) {
      url.searchParams.set("date", day)
    } else {
      url.searchParams.delete("date")
    }

    link.setAttribute("href", url.pathname + url.search)
  }

  onEventClick(info) {
    info.jsEvent.preventDefault()

    if (info.event.url) window.location.assign(info.event.url)
  }

  pillClasses(arg) {
    const role = arg.event.extendedProps.role || "none"

    return [`program-events-calendar__pill--${role}`]
  }

  renderPill(arg) {
    const { startLabel, repeat } = arg.event.extendedProps
    const pill = document.createElement("div")

    pill.className = "program-events-calendar__pill"
    pill.title = arg.event.title

    const dot = document.createElement("span")

    dot.className = "program-events-calendar__pill-dot"

    const time = document.createElement("span")

    time.className = "program-events-calendar__pill-time"
    time.textContent = startLabel

    const title = document.createElement("span")

    title.className = "program-events-calendar__pill-title"
    title.textContent = arg.event.title

    pill.append(dot, time, title)

    if (repeat) {
      const icon = document.createElement("span")
      icon.className = "material-icons program-events-calendar__pill-repeat"
      icon.textContent = "repeat"
      pill.append(icon)
    }

    return { domNodes: [pill] }
  }
}
